'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { Clock, IndianRupee, Star, ChevronLeft, ChevronDown, ChevronUp, Flame, BookOpen, Sparkles, Package, Sun, UserCheck, HelpCircle,  } from 'lucide-react';
import HomeNavbar from '@/app/home-screen/components/HomeNavbar';
import HomeBottomNav from '@/app/home-screen/components/HomeBottomNav';
import { RITUALS_DATA } from '../page';

interface RitualDetail {
  whatIsIt: string;
  whyPerformed: string;
  spiritualSignificance: string;
  materials: string[];
  bestTime: string;
  priestRequirements: string;
  faqs: { question: string; answer: string }[];
}

const RITUAL_DETAILS: Record<string, RitualDetail> = {
  'ganesh-puja': {
    whatIsIt: 'Ganesh Puja is a sacred Hindu ritual dedicated to Lord Ganesha, the elephant-headed deity who is the remover of obstacles and the lord of new beginnings. The puja involves invoking Ganesha through specific mantras, offerings of modak (sweet dumplings), durva grass, and red flowers, followed by aarti and prasad distribution.',
    whyPerformed: 'Ganesh Puja is performed before any auspicious event — be it a business launch, wedding, new home, or important journey. Devotees seek Ganesha\'s blessings to clear obstacles from their path and ensure success in their endeavors. It is also performed on Ganesh Chaturthi and every Wednesday.',
    spiritualSignificance: 'Lord Ganesha represents the union of the individual soul (Atman) with the universal consciousness (Brahman). His elephant head symbolizes wisdom and the ability to overcome all obstacles. Performing this puja aligns the devotee\'s energy with divine intelligence, creating a clear path for success and spiritual growth.',
    materials: ['Ganesh idol or image', 'Red flowers (hibiscus preferred)', 'Durva grass (21 blades)', 'Modak (sweet offering)', 'Coconut', 'Betel leaves and nuts', 'Incense sticks (agarbatti)', 'Camphor (kapoor)', 'Ghee lamp (diya)', 'Turmeric and kumkum', 'Panchamrit (milk, curd, honey, ghee, sugar)', 'Banana and other fruits'],
    bestTime: 'Wednesday mornings between 6 AM and 10 AM are considered most auspicious. Ganesh Chaturthi (4th day of Shukla Paksha in Bhadrapada month) is the most sacred time. Before any new venture or auspicious occasion is also ideal.',
    priestRequirements: 'A priest trained in Ganapati Atharvashirsha and Ganesh Puja Vidhi is required. The priest should be well-versed in Sanskrit mantras and the specific 16-step (Shodashopachara) worship procedure. Experience with Ganesh Chaturthi celebrations is preferred.',
    faqs: [
      { question: 'How long does Ganesh Puja take?', answer: 'A standard Ganesh Puja takes 1–2 hours. A more elaborate Shodashopachara Puja with full Katha can take up to 3 hours.' },
      { question: 'Can Ganesh Puja be performed at home?', answer: 'Yes, absolutely. Ganesh Puja is one of the most commonly performed home rituals. Our priests bring all necessary items or can guide you on what to arrange beforehand.' },
      { question: 'What should I wear during the puja?', answer: 'Traditional attire is recommended — saree or salwar for women, dhoti or kurta-pajama for men. Clean, freshly washed clothes are important as a mark of respect.' },
      { question: 'Is fasting required before Ganesh Puja?', answer: 'Fasting is not mandatory but is considered auspicious. If you choose to fast, avoid non-vegetarian food and alcohol on the day of the puja.' },
      { question: 'Can non-Hindus participate in Ganesh Puja?', answer: 'Yes, Ganesh Puja is open to all who approach with sincerity and devotion. Lord Ganesha is considered a universal deity of wisdom and new beginnings.' },
    ],
  },
  'satyanarayan-puja': {
    whatIsIt: 'Satyanarayan Puja is a devotional ritual dedicated to Lord Vishnu in his form as Satyanarayan — the Lord of Truth. The ceremony includes the recitation of the Satyanarayan Vrat Katha (sacred story), offerings of panchamrit, fruits, and prasad (usually made from banana, milk, and wheat flour).',
    whyPerformed: 'This puja is performed to express gratitude for blessings received and to seek continued divine grace. It is commonly done on full moon days (Purnima), after achieving a significant goal, on birthdays, anniversaries, or during any auspicious occasion. The Katha narrates five stories that emphasize the importance of keeping vows and maintaining faith.',
    spiritualSignificance: 'Satyanarayan represents the eternal truth (Satya) that underlies all existence. Performing this puja is an act of surrendering to divine truth and acknowledging that all achievements come through divine grace. The ritual reinforces the values of honesty, gratitude, and devotion in daily life.',
    materials: ['Vishnu idol or Satyanarayan image', 'Panchamrit ingredients', 'Banana (for prasad)', 'Wheat flour (for prasad)', 'Sugar and milk', 'Yellow flowers', 'Tulsi leaves', 'Incense and camphor', 'Ghee lamp', 'Betel leaves and nuts', 'Fruits and sweets', 'Copper or silver kalash'],
    bestTime: 'Full moon day (Purnima) of any month is most auspicious. Ekadashi (11th day of lunar fortnight) is also favorable. The puja is best performed in the morning or evening hours.',
    priestRequirements: 'A Vaishnava priest trained in Satyanarayan Vrat Katha recitation is required. The priest must know the complete five-chapter Katha in Sanskrit and its regional language translation. Experience with Vishnu Sahasranama and Vishnu Puja Vidhi is essential.',
    faqs: [
      { question: 'How many people should be present?', answer: 'The puja can be performed with just the family, but inviting neighbors and friends is considered auspicious. The more devotees present, the greater the collective merit.' },
      { question: 'What is the prasad for Satyanarayan Puja?', answer: 'The traditional prasad is Sheera (also called Panchamrit Prasad) made from wheat flour, banana, milk, sugar, and ghee. It is considered sacred and should be consumed by all attendees.' },
      { question: 'Can it be performed on any day?', answer: 'While Purnima is most auspicious, the puja can be performed on any day that is significant to the devotee — birthdays, anniversaries, or after achieving an important goal.' },
      { question: 'Do we need to observe a fast?', answer: 'The person performing the vrat (vow) should ideally fast until the puja is complete. Family members may observe partial fasting or eat only sattvic (pure) food.' },
    ],
  },
  'gruhapravesam': {
    whatIsIt: 'Gruhapravesam (also called Vastu Shanti Puja) is the sacred housewarming ceremony performed before entering a new home. It involves purifying the space with Vedic mantras, invoking the blessings of Vastu Devata (deity of the dwelling), and performing a Havan (sacred fire ritual) to cleanse the home of negative energies.',
    whyPerformed: 'A new home carries the energy of construction workers, previous owners, and the land itself. Gruhapravesam purifies this energy and establishes a sacred, harmonious vibration in the space. It ensures that the home becomes a place of peace, prosperity, and spiritual growth for the family.',
    spiritualSignificance: 'In Vedic tradition, a home is not merely a physical structure but a living entity with its own energy field. Gruhapravesam transforms a house into a sacred space (griha) by invoking divine protection and aligning the home\'s energy with cosmic harmony. The ritual establishes the home as a temple of family life.',
    materials: ['Kalash (copper pot with water)', 'Mango leaves', 'Coconut', 'Turmeric and kumkum', 'Rice', 'Havan samagri (fire ritual materials)', 'Ghee', 'Camphor', 'Incense', 'Flowers (marigold and jasmine)', 'Fruits and sweets', 'New pots and pans', 'Milk for boiling over'],
    bestTime: 'Auspicious muhurtas (time windows) calculated by a Jyotishi (astrologer) based on the family\'s horoscope are ideal. Generally, morning hours between 6 AM and 12 PM are preferred. Uttarayan (January to June) is considered more auspicious than Dakshinayan.',
    priestRequirements: 'A priest experienced in Vastu Shanti Puja and Gruhapravesam Vidhi is required. Knowledge of Vastu Shastra principles and the ability to perform Havan are essential. The priest should also be able to calculate or work with an auspicious muhurta.',
    faqs: [
      { question: 'When should Gruhapravesam be performed?', answer: 'It should be performed before the family moves into the new home. An auspicious date and time (muhurta) should be selected based on the family\'s horoscope and Vedic calendar.' },
      { question: 'What happens if we skip Gruhapravesam?', answer: 'While not mandatory, Gruhapravesam is highly recommended in Vedic tradition. It is believed to establish positive energy and divine protection in the home from the very beginning.' },
      { question: 'Can it be done in an apartment?', answer: 'Yes, Gruhapravesam can be performed in any type of dwelling — apartment, villa, or independent house. The ritual is adapted to the available space.' },
      { question: 'How many priests are needed?', answer: 'Typically 2–3 priests are needed for a complete Gruhapravesam with Havan. A simpler version can be performed by one experienced priest.' },
    ],
  },
};

// Default detail for rituals without specific data
const DEFAULT_DETAIL: RitualDetail = {
  whatIsIt: 'This sacred Vedic ritual has been performed for thousands of years as part of the Sanatana Dharma tradition. It involves specific mantras, offerings, and procedures prescribed in ancient scriptures to invoke divine blessings and create positive energy.',
  whyPerformed: 'This ritual is performed to seek divine blessings, remove obstacles, and align the devotee\'s life with cosmic harmony. It marks important transitions and milestones, creating a sacred connection between the individual and the divine.',
  spiritualSignificance: 'Every Vedic ritual carries deep spiritual symbolism. The fire, water, flowers, and mantras used in this ritual represent the five elements and their divine counterparts. Performing this ritual with devotion and understanding accelerates spiritual growth and creates lasting positive karma.',
  materials: ['Sacred items as prescribed by the ritual', 'Flowers and garlands', 'Incense and camphor', 'Ghee lamp (diya)', 'Fruits and sweets', 'Coconut', 'Betel leaves and nuts', 'Turmeric and kumkum', 'Panchamrit ingredients'],
  bestTime: 'Morning hours between sunrise and noon are generally most auspicious. Specific muhurtas (auspicious time windows) can be calculated based on the Vedic calendar and the devotee\'s horoscope.',
  priestRequirements: 'A trained Vedic priest with knowledge of the specific ritual\'s mantras and procedures is required. The priest should be well-versed in Sanskrit and have experience performing this ritual for devotees.',
  faqs: [
    { question: 'How long does this ritual take?', answer: 'The duration varies based on the complexity of the ritual and the number of participants. Our priests will provide an accurate time estimate during booking.' },
    { question: 'What should I prepare in advance?', answer: 'Our priest will provide a complete list of required materials after booking. Many items can be arranged by the priest, or we can guide you on what to purchase.' },
    { question: 'Can this ritual be performed at home?', answer: 'Yes, most Vedic rituals can be performed at home. Our priests are experienced in adapting rituals to home settings while maintaining their authenticity.' },
    { question: 'Is there a specific dress code?', answer: 'Traditional attire is recommended — saree or salwar for women, dhoti or kurta for men. Clean, freshly washed clothes are important as a mark of respect.' },
  ],
};

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`border rounded-xl transition-all duration-200 ${open ? 'border-saffron/40 bg-saffron/5' : 'border-gold/20 bg-white'}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left"
      >
        <span className="text-sm font-semibold text-maroon pr-4">{question}</span>
        {open ? <ChevronUp size={16} className="text-saffron flex-shrink-0" /> : <ChevronDown size={16} className="text-maroon/40 flex-shrink-0" />}
      </button>
      {open && (
        <div className="px-5 pb-4">
          <p className="text-sm text-maroon/70 font-body leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
}

const SECTION_ICONS: Record<string, React.ReactNode> = {
  what: <BookOpen size={18} className="text-saffron" />,
  why: <Flame size={18} className="text-saffron" />,
  spiritual: <Sparkles size={18} className="text-saffron" />,
  materials: <Package size={18} className="text-saffron" />,
  bestTime: <Sun size={18} className="text-saffron" />,
  priest: <UserCheck size={18} className="text-saffron" />,
  faq: <HelpCircle size={18} className="text-saffron" />,
};

export default function RitualDetailPage() {
  const params = useParams();
  const id = params?.id as string;

  const ritual = RITUALS_DATA.find((r) => r.id === id);
  const detail = RITUAL_DETAILS[id] || DEFAULT_DETAIL;

  if (!ritual) {
    return (
      <div className="min-h-screen bg-ivory flex flex-col items-center justify-center">
        <HomeNavbar />
        <div className="text-center pt-24">
          <div className="text-5xl mb-4">🙏</div>
          <h2 className="font-display text-2xl font-bold text-maroon mb-2">Ritual Not Found</h2>
          <p className="text-maroon/60 text-sm mb-6">This ritual may have been moved or doesn't exist.</p>
          <Link href="/rituals" className="bg-maroon text-ivory text-sm font-semibold px-6 py-3 rounded-full btn-glow">
            Browse All Rituals
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ivory overflow-x-hidden">
      <HomeNavbar />

      {/* Hero */}
      <div className="relative pt-16 min-h-[50vh] overflow-hidden">
        <img
          src={ritual.image}
          alt={ritual.alt}
          className="absolute inset-0 w-full h-full object-cover"
          onError={(e) => { (e.target as HTMLImageElement).src = '/assets/images/no_image.png'; }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />

        {/* Back button */}
        <div className="relative z-10 max-w-screen-lg mx-auto px-4 sm:px-6 pt-6">
          <Link
            href="/rituals"
            className="inline-flex items-center gap-2 text-ivory/80 hover:text-ivory text-sm font-medium transition-colors"
          >
            <ChevronLeft size={16} />
            All Rituals
          </Link>
        </div>

        {/* Hero content */}
        <div className="relative z-10 max-w-screen-lg mx-auto px-4 sm:px-6 pb-10 pt-6">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="text-[10px] font-bold uppercase tracking-widest text-gold/90 bg-black/30 px-3 py-1 rounded-full">
              {ritual.category}
            </span>
            {ritual.popular && (
              <span className="flex items-center gap-1 text-[10px] font-bold text-white bg-saffron px-3 py-1 rounded-full">
                <Star size={9} fill="white" /> Popular
              </span>
            )}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-ivory mb-1">
            {ritual.name}
          </h1>
          <p className="text-gold/80 text-base italic font-body mb-3">{ritual.sanskrit}</p>
          <p className="text-ivory/80 text-sm font-body max-w-xl mb-5">{ritual.description}</p>

          {/* Quick stats */}
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2 bg-black/30 backdrop-blur-sm rounded-xl px-3 py-2">
              <Clock size={14} className="text-gold" />
              <span className="text-xs font-semibold text-ivory">{ritual.duration}</span>
            </div>
            <div className="flex items-center gap-2 bg-black/30 backdrop-blur-sm rounded-xl px-3 py-2">
              <IndianRupee size={14} className="text-gold" />
              <span className="text-xs font-semibold text-ivory">From ₹{ritual.startingPrice.toLocaleString('en-IN')}</span>
            </div>
            <div className="flex items-center gap-2 bg-black/30 backdrop-blur-sm rounded-xl px-3 py-2">
              <Star size={14} className="text-gold fill-gold" />
              <span className="text-xs font-semibold text-ivory">{ritual.rating} ({ritual.reviewCount} reviews)</span>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Book Now bar */}
      <div className="sticky top-16 z-40 bg-white/95 backdrop-blur-xl border-b border-gold/15 shadow-sm">
        <div className="max-w-screen-lg mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div>
            <p className="text-xs text-maroon/50 font-body">Starting from</p>
            <div className="flex items-center gap-0.5">
              <IndianRupee size={14} className="text-maroon font-bold" />
              <span className="text-lg font-bold text-maroon">{ritual.startingPrice.toLocaleString('en-IN')}</span>
            </div>
          </div>
          <Link
            href="/booking-flow"
            className="bg-saffron text-white text-sm font-bold px-6 py-3 rounded-full btn-glow shadow-lg hover:bg-maroon transition-colors duration-200"
          >
            Book Now
          </Link>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-screen-lg mx-auto px-4 sm:px-6 py-10 pb-28 md:pb-16 space-y-8">

        {/* What is this ritual */}
        <section className="bg-white rounded-2xl border border-gold/20 p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            {SECTION_ICONS.what}
            <h2 className="font-display text-xl font-bold text-maroon">What is this Ritual?</h2>
          </div>
          <p className="text-sm text-maroon/75 font-body leading-relaxed">{detail.whatIsIt}</p>
        </section>

        {/* Why performed */}
        <section className="bg-white rounded-2xl border border-gold/20 p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            {SECTION_ICONS.why}
            <h2 className="font-display text-xl font-bold text-maroon">Why is it Performed?</h2>
          </div>
          <p className="text-sm text-maroon/75 font-body leading-relaxed">{detail.whyPerformed}</p>
        </section>

        {/* Spiritual significance */}
        <section className="bg-gradient-to-br from-saffron/8 to-amber-50 rounded-2xl border border-saffron/20 p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            {SECTION_ICONS.spiritual}
            <h2 className="font-display text-xl font-bold text-maroon">Spiritual Significance</h2>
          </div>
          <p className="text-sm text-maroon/75 font-body leading-relaxed">{detail.spiritualSignificance}</p>
        </section>

        {/* Two column: Duration + Best Time */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <section className="bg-white rounded-2xl border border-gold/20 p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <Clock size={18} className="text-saffron" />
              <h2 className="font-display text-lg font-bold text-maroon">Duration</h2>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl saffron-gradient flex items-center justify-center flex-shrink-0">
                <Clock size={22} className="text-white" />
              </div>
              <div>
                <p className="text-xl font-bold text-maroon">{ritual.duration}</p>
                <p className="text-xs text-maroon/50 font-body">Approximate duration</p>
              </div>
            </div>
          </section>

          <section className="bg-white rounded-2xl border border-gold/20 p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              {SECTION_ICONS.bestTime}
              <h2 className="font-display text-lg font-bold text-maroon">Best Time</h2>
            </div>
            <p className="text-sm text-maroon/75 font-body leading-relaxed">{detail.bestTime}</p>
          </section>
        </div>

        {/* Materials required */}
        <section className="bg-white rounded-2xl border border-gold/20 p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            {SECTION_ICONS.materials}
            <h2 className="font-display text-xl font-bold text-maroon">Materials Required</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {detail.materials.map((item, i) => (
              <div key={i} className="flex items-center gap-2.5 py-2 border-b border-gold/10 last:border-0">
                <div className="w-1.5 h-1.5 rounded-full bg-saffron flex-shrink-0" />
                <span className="text-sm text-maroon/75 font-body">{item}</span>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-maroon/50 font-body italic">
            * Our priests can arrange most materials. You will receive a detailed list after booking.
          </p>
        </section>

        {/* Priest requirements */}
        <section className="bg-gradient-to-br from-maroon/5 to-rose-50 rounded-2xl border border-maroon/15 p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            {SECTION_ICONS.priest}
            <h2 className="font-display text-xl font-bold text-maroon">Priest Requirements</h2>
          </div>
          <p className="text-sm text-maroon/75 font-body leading-relaxed">{detail.priestRequirements}</p>
          <div className="mt-4 flex items-center gap-3 bg-white/60 rounded-xl px-4 py-3 border border-maroon/10">
            <div className="w-8 h-8 rounded-full bg-maroon/10 flex items-center justify-center flex-shrink-0">
              <UserCheck size={16} className="text-maroon" />
            </div>
            <p className="text-xs text-maroon/70 font-body">
              All Yagnick priests are verified, background-checked, and trained in authentic Vedic traditions.
            </p>
          </div>
        </section>

        {/* FAQs */}
        <section>
          <div className="flex items-center gap-3 mb-5">
            {SECTION_ICONS.faq}
            <h2 className="font-display text-xl font-bold text-maroon">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-3">
            {detail.faqs.map((faq, i) => (
              <FAQItem key={i} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </section>

        {/* Book Now CTA */}
        <section className="relative overflow-hidden rounded-2xl hero-gradient p-8 text-center shadow-xl">
          <div className="absolute inset-0 opacity-10 pointer-events-none">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 animate-rotate-slow"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='100' cy='100' r='90' fill='none' stroke='%23D4AF37' stroke-width='1'/%3E%3Ccircle cx='100' cy='100' r='60' fill='none' stroke='%23D4AF37' stroke-width='1'/%3E%3C/svg%3E")`,
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
              }}
            />
          </div>
          <div className="relative z-10">
            <div className="text-3xl mb-3">🪔</div>
            <h3 className="font-display text-2xl font-bold text-ivory mb-2">Ready to Book {ritual.name}?</h3>
            <p className="text-ivory/70 text-sm font-body mb-6 max-w-md mx-auto">
              Connect with a verified priest and experience this sacred ritual in the comfort of your home.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/booking-flow"
                className="bg-saffron text-white font-bold px-8 py-3.5 rounded-full btn-glow shadow-lg hover:bg-amber-500 transition-colors duration-200 text-sm"
              >
                Book Now — From ₹{ritual.startingPrice.toLocaleString('en-IN')}
              </Link>
              <Link
                href="/rituals"
                className="border border-ivory/40 text-ivory/80 hover:text-ivory hover:border-ivory text-sm font-medium px-6 py-3.5 rounded-full transition-all duration-200"
              >
                Explore Other Rituals
              </Link>
            </div>
          </div>
        </section>

      </main>

      <HomeBottomNav />
    </div>
  );
}
